import { css } from 'styled-components';
import { ButtonsTheme } from '@/themes/jl-money/types';
import { colors } from '@/themes/jl-money/constants/colors';
import { typography } from '@/themes/jl-money/constants/typography';

export type BaseButtonProps = {
  fullWidth?: boolean;
  disabled?: boolean;
  styling?: Styling;
  loading?: boolean;
};

export type Styling = 'secondary' | 'primary' | 'link';

export const buttonStyleJL = css<BaseButtonProps>`
  outline: 0;
  cursor: pointer;
  align-items: center;
  display: inline-flex;
  text-decoration: none;
  box-sizing: border-box;
  padding: 8px 16px;
  width: ${({ fullWidth = false }) => fullWidth && '100%'};

  ${({ styling = 'primary', theme }) => {
    const { borderRadius, text } = (theme as { button?: ButtonsTheme })?.button || {};
    const { height, weight, size } = text || {};
    const buttonTheme: ButtonsTheme = {
      secondary: {
        active: {
          border: `2px solid ${colors.jlMoneyGreenDark}`,
          text: colors.jlMoneyGreenDark,
          bg: colors.jlMoneyGreenLight,
          opacity: '1',
        },
        focus: {
          boxShadow: `0 0 0 2px ${colors.white}, 0 0 0 4px ${colors.jlMoneyBlue}`,
          border: `2px solid ${colors.jlMoneyGreenDark}`,
        },
        hover: {
          border: `2px solid ${colors.jlMoneyGreenDark}`,
          text: colors.jlMoneyGreenDark,
          bg: colors.jlMoneyBeige,
        },
        disabled: {
          border: `2px solid ${colors.jlMoneyGrey}`,
          bg: colors.jlMoneyGreyLight,
          text: colors.jlMoneyGrey,
        },
        border: `2px solid ${colors.jlMoneyGreenDark}`,
        text: colors.jlMoneyGreenDark,
        borderRadius: '4px',
        bg: colors.white,
      },
      primary: {
        active: {
          border: `2px solid ${colors.jlMoneyGreenDark}`,
          text: colors.jlMoneyGreenDark,
          bg: colors.jlMoneyGreenLight,
          opacity: '1',
        },
        focus: {
          boxShadow: `0 0 0 2px ${colors.white}, 0 0 0 4px ${colors.jlMoneyBlue}`,
          border: `2px solid ${colors.jlMoneyGreenDark}`,
        },
        hover: {
          border: `2px solid ${colors.jlMoneyGreenDark}`,
          text: colors.jlMoneyGreenDark,
          bg: colors.white,
        },
        disabled: {
          text: colors.jlMoneyGreyDark,
          bg: colors.jlMoneyGreyLight,
        },
        border: `2px solid ${colors.jlMoneyGreenDark}`,
        text: colors.jlMoneyGreenNeon,
        bg: colors.jlMoneyGreenDark,
      },
      link: {
        hover: {
          border: `2px solid ${colors.jlMoneyGreenDark}`,
          text: colors.jlMoneyGreenDark,
          bg: colors.white,
        },
        focus: {
          boxShadow: `0 0 0 2px ${colors.white}, 0 0 0 4px ${colors.jlMoneyBlue}`,
          border: `2px solid transparent`,
        },
        border: `2px solid transparent`,
        text: colors.greyDarkest,
        bg: 'transparent',
      },
      text: {
        weight: typography?.weights?.extraBold,
        height: typography.lineHeight.lead,
        size: typography.text.sizes.lead,
      },
      borderRadius: `6px`,
      padding: '8px 16px',
    };

    const { text: textColor, bg: bgColor, disabled, border, active, hover, focus } = buttonTheme[styling] || {};
    return css`
      background: ${bgColor};
      border-radius: ${borderRadius ?? '6px'};
      border: ${border ?? '2px solid transparent'};
      color: ${textColor ?? colors.jlMoneyGreenNeon};
      font-size: ${size ?? typography.text.sizes.lead};
      font-weight: ${weight ?? typography.weights.extraBold};
      line-height: ${height ?? typography.lineHeight.lead};

      &:hover {
        color: ${hover?.text};
        background: ${hover?.bg};
        border: ${hover?.border};
      }

      &:disabled {
        color: ${disabled?.text};
        background: ${disabled?.bg};
        border: ${disabled?.border};
        cursor: not-allowed;
      }

      &:active {
        color: ${active?.text};
        background: ${active?.bg};
        border: ${active?.border};
        opacity: ${active?.opacity};
      }

      &:focus {
        border: ${focus?.border};
        box-shadow: ${focus?.boxShadow};
      }
    `;
  }}
`;
